/**
 * Which tenant this page is serving — asked of the server, never guessed.
 *
 * One frontend build serves every tenant (REACT_APP_BACKEND_URL stays unset,
 * the API is same-origin), so the bundle cannot carry a tenant's name, its
 * products or which internal-only widgets it shows. GET /api/tenant/config
 * (server/app/api/v1/tenant_config.py) answers that from the tenant's own env:
 *
 *   { brand, status_page_url, products: [names, catalog order], features: [flags] }
 *
 * Feature flags (server env TENANT_FEATURES, comma-separated, lowercase):
 *   session_stats  per-turn count / latency / cost readout + hover breakdown
 *                  (TSEDashboard2 header, dashboard5 CopilotPanel, BatchMode thread header)
 *   legal_links    Privacy Policy / Terms of Service links (Login, About Us)
 *
 * DOs
 *  - Gate a tenant-specific widget on a flag here: add a FEATURE_* constant,
 *    list it above, and have the coordinator set it in that tenant's env.
 *  - Use useTenantConfig()/useTenantFeature() in components; they render the
 *    safe default first and re-render once the config arrives.
 *
 * DON'Ts
 *  - Don't read window.location.hostname to decide a feature — that is the
 *    per-tenant hardcoding this module replaced.
 *  - Don't default a failed call to any tenant's values: failure = no
 *    features, empty brand, empty catalog.
 *  - Don't expect anything private in this config: the endpoint is public and
 *    is called before sign-in (Login brands itself from it).
 */
import { useEffect, useState } from "react";
import { getApiUrl } from "../utils/api";

export const TENANT_CONFIG_PATH = "/api/tenant/config";

export const FEATURE_SESSION_STATS = "session_stats";
export const FEATURE_LEGAL_LINKS = "legal_links";

export const DEFAULT_TENANT_CONFIG = Object.freeze({
  brand: "",
  status_page_url: "",
  products: Object.freeze([]),
  features: Object.freeze([]),
});

let cached = null;
let inflight = null;

const strings = (v) => (Array.isArray(v) ? v.map((s) => String(s ?? "").trim()).filter(Boolean) : []);

function normalize(raw) {
  const body = raw && typeof raw === "object" ? raw : {};
  return Object.freeze({
    brand: String(body.brand ?? "").trim(),
    status_page_url: String(body.status_page_url ?? "").trim(),
    products: Object.freeze(strings(body.products)),
    features: Object.freeze([...new Set(strings(body.features).map((f) => f.toLowerCase()))]),
  });
}

/** Resolves to the tenant config. Fetched once per page load; a failure is not cached. */
export function loadTenantConfig() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    // Promise.resolve().then: a fetch that throws synchronously (none defined,
    // as under jsdom) becomes a rejection, i.e. the safe default — not a crash.
    inflight = Promise.resolve()
      .then(() => fetch(getApiUrl(TENANT_CONFIG_PATH), { headers: { Accept: "application/json" } }))
      .then((res) => {
        if (!res.ok) throw new Error(`tenant config ${res.status}`);
        return res.json();
      })
      .then((body) => {
        cached = normalize(body);
        return cached;
      })
      .catch(() => DEFAULT_TENANT_CONFIG)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** The config if it has already arrived, else the safe default. Synchronous. */
export function getTenantConfig() {
  return cached || DEFAULT_TENANT_CONFIG;
}

export function hasFeature(config, name) {
  return !!config && Array.isArray(config.features) && config.features.includes(String(name).toLowerCase());
}

export function useTenantConfig() {
  const [config, setConfig] = useState(getTenantConfig);
  useEffect(() => {
    if (cached) {
      if (config !== cached) setConfig(cached);
      return undefined;
    }
    let alive = true;
    loadTenantConfig().then((c) => {
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return config;
}

export function useTenantFeature(name) {
  return hasFeature(useTenantConfig(), name);
}

/** The Active Incident quick action's question, naming the tenant's brand when it has one. */
export function incidentsQuery(brand) {
  const b = String(brand ?? "").trim();
  return `Are there any active ${b ? `${b} ` : ""}service incidents right now?`;
}

export function __resetTenantConfigForTests() {
  cached = null;
  inflight = null;
}
